import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { PageHeader, Loader, StatCard } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { DndConfigForm } from "@/components/dnd/DndConfigForm";
import { api, apiError } from "@/lib/api";
import { fmtPKR, fmtDate } from "@/lib/format";

const Card = ({ title, children, testid }) => (
  <div className="ec-card p-6" data-testid={testid}><h3 className="font-head text-lg font-semibold text-white mb-4">{title}</h3>{children}</div>
);

const GameRow = ({ g }) => (
  <div className="py-3 text-sm" data-testid={`dnd-admin-game-${g.game_no}`}>
    <div className="flex flex-wrap justify-between gap-2">
      <span className="text-white">#{g.game_no} · {g.user_name} · {fmtPKR(g.amount)}</span>
      <span className={g.status === "completed" ? "text-emerald-400" : "text-yellow-500"}>
        {g.status === "completed" ? `${g.decision?.toUpperCase()} · ${fmtPKR(g.payout)}` : g.status.replace("_", " ")}
      </span>
    </div>
    <p className="text-[11px] text-gray-500">Box {g.own_box ?? "-"} · opened {g.opened_count} · offers {g.offers.map((o) => `${fmtPKR(o.amount)} ${o.decision === "deal" ? "✓" : "✗"}`).join(", ") || "-"} · {fmtDate(g.completed_at || g.created_at)}</p>
  </div>
);

export default function AdminDnd() {
  const [data, setData] = useState(null);
  const [games, setGames] = useState([]);
  const load = () => {
    api.get("/admin/dnd").then(({ data }) => setData(data)).catch(() => {});
    api.get("/admin/dnd/games").then(({ data }) => setGames(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!data) return <Loader />;
  const s = data.stats;
  const active = games.filter((g) => g.status !== "completed");
  const done = games.filter((g) => g.status === "completed");
  const exportPdf = async () => {
    try {
      const res = await api.get("/admin/dnd/report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "deal-or-no-deal-report.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const maxDist = Math.max(1, ...s.prize_distribution.map((d) => d[1]));
  return (
    <div>
      <PageHeader title="Deal or No Deal Management" subtitle="Prizes, banker offers, rules and full game reporting."
        action={<Button onClick={exportPdf} className="btn-glow" data-testid="dnd-export-pdf"><FileDown className="w-4 h-4 me-2" />Export PDF</Button>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="dnd-stat-games" label="Total Games" value={s.total_games} sub={`${s.completed_games} completed · ${s.active_games} active`} />
        <StatCard testid="dnd-stat-participants" label="Participants" value={s.participants} />
        <StatCard testid="dnd-stat-boxes" label="Boxes Opened" value={s.boxes_opened} />
        <StatCard testid="dnd-stat-offers" label="Offers Made" value={s.offers_made} sub={`${s.offers_accepted} accepted · ${s.offers_rejected} rejected`} />
        <StatCard testid="dnd-stat-entries" label="Total Entries" value={fmtPKR(s.total_amount)} />
        <StatCard testid="dnd-stat-payouts" label="Total Prizes Paid" value={fmtPKR(s.total_payout)} />
        <StatCard testid="dnd-stat-decisions" label="Deal / Keep / Swap" value={`${s.decisions.deal} / ${s.decisions.keep} / ${s.decisions.swap}`} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <DndConfigForm key={JSON.stringify(data.config)} cfg={data.config} onSaved={load} />
        <div className="space-y-6">
          <Card title="Final Prize Distribution" testid="dnd-distribution">
            {s.prize_distribution.length === 0 ? <p className="text-gray-500 text-sm text-center py-6">No completed games yet.</p> :
              s.prize_distribution.map(([k, v]) => (
                <div key={k} className="flex items-center gap-3 text-xs py-1">
                  <span className="w-24 text-gray-500 keep-ltr">Rs {k}</span>
                  <div className="flex-1 h-3 rounded bg-white/5"><div className="h-3 rounded bg-gradient-to-r from-orange-600 to-yellow-500" style={{ width: `${(v / maxDist) * 100}%` }} /></div>
                  <span className="w-8 text-end text-white">{v}</span>
                </div>
              ))}
          </Card>
          <Card title={`Active Games (${active.length})`} testid="dnd-active-games">
            {active.length === 0 ? <p className="text-gray-500 text-sm text-center py-6">No active games.</p> :
              <div className="divide-y divide-white/10 max-h-72 overflow-y-auto">{active.map((g) => <GameRow key={g.id} g={g} />)}</div>}
          </Card>
        </div>
      </div>
      <div className="mt-6">
        <Card title={`Completed Games (${done.length})`} testid="dnd-completed-games">
          {done.length === 0 ? <p className="text-gray-500 text-sm text-center py-6">No completed games yet.</p> :
            <div className="divide-y divide-white/10 max-h-[32rem] overflow-y-auto">{done.map((g) => <GameRow key={g.id} g={g} />)}</div>}
        </Card>
      </div>
    </div>
  );
}
